import React from 'react';
import { Check, ChevronDown, ChevronRight, Database, Download, Folder, FolderOpen, Loader2, Search, Table, Trash2, RefreshCw, Settings, Unplug, Plus, ShieldAlert } from 'lucide-react';
import { cn } from '../../lib/utils';

/** Small dot + tooltip for in-progress dataset rows (failed rows use the “Import failed” badge and inline copy). */
const StatusBadge = ({ status, className = "" }) => {
  return (
    <div className={cn("relative group shrink-0", className)}>
      <div className="w-1.5 h-1.5 rounded-full bg-amber-500/60 dark:bg-amber-400/60 animate-pulse" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-[10px] font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-10 shadow-lg">
        {status === 'loading' ? 'Loading' : status === 'generating' ? 'Generating' : status || 'Processing'}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100" />
      </div>
    </div>
  );
};

const formatCompactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat(undefined, {
    notation: n >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: 1
  }).format(n);
};

const getDatasetMetadataValues = (item) => {
  const rowCount = item.stats?.num_rows ?? item.statistics?.num_rows;
  const formattedRows = rowCount == null ? '—' : formatCompactNumber(rowCount);
  const columnCount =
    typeof item.column_count === 'number'
      ? item.column_count
      : Array.isArray(item.schema)
        ? item.schema.length
        : null;
  const hasSpatial =
    typeof item.has_spatial === 'boolean'
      ? item.has_spatial
      : (Array.isArray(item.spatial_columns) ? item.spatial_columns.length : 0) > 0;
  const hasTemporal =
    typeof item.has_temporal === 'boolean'
      ? item.has_temporal
      : (Array.isArray(item.temporal_columns) ? item.temporal_columns.length : 0) > 0;

  return {
    rows: formattedRows || '—',
    columns: columnCount ? formatCompactNumber(columnCount) : '—',
    hasSpatial,
    hasTemporal
  };
};

const GroupRow = ({
  label,
  count,
  isExpanded,
  onToggle,
  icon: Icon,
  iconColor = "text-blue-500",
  level = 0,
  onSettings,
  onUnlink,
  onAdd,
  onReauth,
  sourceLabel = null,
  reauthRequired = false,
  disabled = false,
  disabledLabel = null,
  columnSpan = 2
}) => {
  const bgClass = level === 0
    ? "bg-gray-50/40 dark:bg-gray-950/30 hover:bg-gray-100/50 dark:hover:bg-gray-900/40"
    : "bg-gray-50/20 dark:bg-gray-950/20 hover:bg-gray-100/40 dark:hover:bg-gray-900/30";

  return (
    <tr
      className={cn(bgClass, "cursor-pointer border-b border-gray-100/60 dark:border-gray-800/60 transition-colors")}
      onClick={onToggle}
    >
      <td className={cn("px-5 py-2", level > 0 && "py-1.5")} colSpan={columnSpan}>
        <div className="flex items-center gap-2" style={{ paddingLeft: level === 0 ? '0' : '2rem' }}>
          {isExpanded ? (
            <ChevronDown size={level === 0 ? 12 : 11} className="text-gray-400" />
          ) : (
            <ChevronRight size={level === 0 ? 12 : 11} className="text-gray-400" />
          )}
          {level === 0 ? (
            <Database size={13} strokeWidth={2.5} className={iconColor} />
          ) : isExpanded ? (
            <FolderOpen size={12} className={iconColor} />
          ) : (
            <Folder size={12} className={iconColor} />
          )}
          <span className={cn(
            "font-black uppercase tracking-wider",
            level === 0 ? "text-[10px] text-gray-700 dark:text-gray-300" : "text-[9px] text-gray-600 dark:text-gray-400"
          )}>
            {label}
          </span>
          {sourceLabel && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[7px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50">
              {sourceLabel}
            </span>
          )}
          {disabled && disabledLabel && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[7px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50">
              {disabledLabel}
            </span>
          )}
          <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
            ({count})
          </span>
          {reauthRequired && onReauth ? (
            <div className="ml-1 flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReauth();
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[8px] font-black uppercase tracking-widest text-red-700 hover:bg-red-100 dark:border-red-800/60 dark:bg-red-900/25 dark:text-red-300 dark:hover:bg-red-900/40 transition-colors"
                title="Reconnect BigQuery"
                aria-label="Reconnect BigQuery"
              >
                <ShieldAlert size={11} />
                Re-auth required
              </button>
            </div>
          ) : !disabled && (onAdd || onSettings || onUnlink) && (
            <div className="ml-1 flex items-center gap-1 opacity-80 transition-opacity group-hover:opacity-100">
              {onAdd && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAdd();
                  }}
                  className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                  title="Add from source"
                  aria-label={`Add from ${label}`}
                >
                  <Plus size={12} />
                </button>
              )}
              {onSettings && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSettings();
                  }}
                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title="Connection settings"
                  aria-label={`Open settings for ${label}`}
                >
                  <Settings size={12} />
                </button>
              )}
              {onUnlink && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnlink();
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Unlink connection"
                  aria-label={`Unlink ${label}`}
                >
                  <Unplug size={12} />
                </button>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const ItemRow = ({
  item,
  onSelect,
  onRemove,
  onDownload,
  onRetry,
  formatDate,
  level = 0,
  showStatus = false,
  status = null,
  getDisplayName = (item) => item.displayName || item.name,
  showMetadataColumns = false,
  uppercaseItemName = true,
  disabled = false,
  disabledReason = null,
  isRemoving = false
}) => {
  // Level 0: pl-6 (top level), Level 1: pl-14 (items in local groups), Level 2: pl-24 (items in datasets)
  const canOpenPreview = !disabled && (status == null || status === 'ready' || status === 'failed');
  const canDownload =
    Boolean(onDownload) && item.source === 'manteo' && status === 'ready';
  const isBusy = status === 'loading' || status === 'generating';
  const primaryName = item.name || '';
  const secondaryName = getDisplayName(item) || '';
  const normalizedPrimary = String(primaryName).trim().toLowerCase();
  const normalizedSecondary = String(secondaryName).trim().toLowerCase();
  const shouldShowSecondary = normalizedSecondary && normalizedSecondary !== normalizedPrimary;
  const metadata = getDatasetMetadataValues(item);

  return (
    <tr
      onClick={(e) => {
        e.stopPropagation();
        if (canOpenPreview && onSelect) {
          onSelect(item);
        }
      }}
      className={cn(
        "group transition-all border-b border-gray-50/50 dark:border-gray-800/30",
        canOpenPreview ? "hover:bg-emerald-50/20 dark:hover:bg-emerald-900/5 cursor-pointer" : cn(isBusy && "opacity-80", "cursor-not-allowed")
      )}
    >
      <td className="px-5 py-2">
        <div className="flex items-center gap-2" style={{ paddingLeft: level === 0 ? '0' : level === 1 ? '2rem' : '4.5rem' }}>
          {showStatus && (status === 'loading' || status === 'generating') && (
            <StatusBadge status={status} />
          )}
          <div className="flex items-center gap-1">
            {showStatus && status && status !== 'ready' && status !== 'failed' && onRetry && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry(item.id);
                }}
                className="p-1.5 text-gray-500 hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400 transition-colors bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                title="Retry"
              >
                <RefreshCw size={12} strokeWidth={2.5} className="animate-spin" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (canOpenPreview && onSelect) {
                  onSelect(item);
                }
              }}
              disabled={!canOpenPreview}
              className={cn(
                "p-1.5 rounded-lg transition-all",
                canOpenPreview
                  ? "text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:text-gray-300 dark:hover:text-emerald-400 dark:hover:bg-emerald-900/20"
                  : "text-gray-200 dark:text-gray-700 cursor-not-allowed"
              )}
              title="View"
              aria-label={`View ${primaryName}`}
            >
              <Search size={13} strokeWidth={2.5} />
            </button>
            {canDownload && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(item.id, item);
                }}
                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                title="Download Parquet"
                aria-label={`Download ${primaryName}`}
              >
                <Download size={13} strokeWidth={2.5} />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled && !isBusy && !isRemoving) onRemove?.(item.id, item);
              }}
              disabled={disabled || isBusy || isRemoving}
              className={cn(
                'p-1.5 rounded-lg transition-all',
                disabled || isBusy
                  ? 'text-gray-200 dark:text-gray-700 cursor-not-allowed'
                  : isRemoving
                    ? 'text-gray-500 dark:text-gray-400 cursor-wait'
                    : 'text-gray-500 hover:text-red-500 hover:bg-red-50 dark:text-gray-300 dark:hover:bg-red-900/20'
              )}
              title={
                isRemoving
                  ? 'Removing…'
                  : isBusy
                    ? 'Unavailable while dataset is loading'
                    : 'Remove'
              }
              aria-label={isRemoving ? `Removing ${primaryName}` : `Remove ${primaryName}`}
            >
              {isRemoving ? (
                <Loader2 size={13} strokeWidth={2.5} className="animate-spin" />
              ) : (
                <Trash2 size={13} strokeWidth={2.5} />
              )}
            </button>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "text-[11px] font-bold text-gray-900 dark:text-white tracking-wider block truncate max-w-[300px]",
                  uppercaseItemName && "uppercase"
                )}
              >
                {primaryName}
              </span>
              {showStatus && status === 'ready' && (
                <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[7px] font-black uppercase tracking-widest text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300">
                  Ready
                </span>
              )}
              {showStatus && status === 'failed' && (
                <span
                  className="shrink-0 rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[7px] font-black uppercase tracking-widest text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-300"
                  title={
                    (item.summary && String(item.summary).trim()) || 'Import failed'
                  }
                >
                  Import failed
                </span>
              )}
              {disabled && (
                <span
                  className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[7px] font-black uppercase tracking-widest text-amber-700 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-300"
                  title={disabledReason || 'Upgrade required'}
                >
                  Upgrade required
                </span>
              )}
            </div>
            {shouldShowSecondary && (
              <span className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block max-w-[300px] mt-0.5 opacity-60">
                {secondaryName}
              </span>
            )}
            {showStatus && status === 'failed' && (
              <p className="mt-1.5 max-w-[min(520px,88vw)] text-[9px] font-medium leading-snug text-red-700 dark:text-red-300">
                {(item.summary && String(item.summary).trim()) || 'No error message was recorded.'}
              </p>
            )}
          </div>
        </div>
      </td>
      {showMetadataColumns && (
        <>
          <td className="px-5 py-2">
            <span className="text-[9px] font-mono font-bold text-gray-600 dark:text-gray-300">
              {metadata.rows}
            </span>
          </td>
          <td className="px-5 py-2">
            <span className="text-[9px] font-mono font-bold text-gray-600 dark:text-gray-300">
              {metadata.columns}
            </span>
          </td>
          <td className="px-5 py-2">
            <span className="flex h-4 items-center text-gray-500 dark:text-gray-400" title={metadata.hasSpatial ? 'Spatial columns detected' : 'No spatial columns'}>
              {metadata.hasSpatial ? <Check size={12} strokeWidth={3} /> : '—'}
            </span>
          </td>
          <td className="px-5 py-2">
            <span className="flex h-4 items-center text-gray-500 dark:text-gray-400" title={metadata.hasTemporal ? 'Temporal columns detected' : 'No temporal columns'}>
              {metadata.hasTemporal ? <Check size={12} strokeWidth={3} /> : '—'}
            </span>
          </td>
        </>
      )}
      <td className="px-5 py-2">
        <span className="text-[9px] font-mono text-gray-500 dark:text-gray-400">
          {formatDate(item.created_at)}
        </span>
      </td>
    </tr>
  );
};

const TreeTable = ({
  // Data
  bigQueryTree = {},
  localGroups = {},
  customGroup = null,

  // State
  expandedProjects = new Set(),
  expandedDatasets = new Set(),
  expandedLocalGroups = new Set(),

  // Handlers
  onToggleProject,
  onToggleDataset,
  onToggleLocalGroup,
  onProjectSettings,
  onProjectUnlink,
  onProjectReauth,
  onProjectAdd,
  onLocalGroupAdd,
  onSelectItem,
  onRemoveItem,
  onDownloadItem,
  onRetryItem,

  // Configuration
  formatDate,
  itemIcon = Table,
  projectIconColor = "text-blue-500",
  datasetIconColor = "text-blue-400",
  localGroupIconColor = "text-emerald-500",
  showStatus = false,
  getItemStatus = (item) => item.status,
  getDisplayName = (item) => item.displayName || item.name,
  getItemName = (item) => item.name || item.functionName,
  showMetadataColumns = false,
  uppercaseItemName = true,
  getLocalGroupLabel = (groupKey) => groupKey,
  getLocalSubgroupLabel = (_parentKey, subKey) => subKey,
  onLocalSubgroupAdd = null,
  getLocalSubgroupEmptyMessage = null,
  bigQueryReauthRequired = false,
  isBigQueryLocked = false,
  removingItemId = null
}) => {
  const columnSpan = showMetadataColumns ? 6 : 2;

  const isNestedLocalGroup = value =>
    value && typeof value === 'object' && !Array.isArray(value);

  const renderFlatLocalGroup = (groupKey, items) => {
    const isGroupExpanded = expandedLocalGroups.has(groupKey);

    return (
      <React.Fragment key={groupKey}>
        <GroupRow
          label={getLocalGroupLabel(groupKey)}
          count={items.length}
          isExpanded={isGroupExpanded}
          onToggle={() => onToggleLocalGroup(groupKey)}
          onAdd={onLocalGroupAdd ? () => onLocalGroupAdd(groupKey) : null}
          icon={Folder}
          iconColor={localGroupIconColor}
          level={0}
          columnSpan={columnSpan}
        />

        {isGroupExpanded && items.map(item => (
          <ItemRow
            key={item.id}
            item={item}
            onSelect={onSelectItem}
            onRemove={onRemoveItem}
            onDownload={onDownloadItem}
            onRetry={onRetryItem}
            formatDate={formatDate}
            itemIcon={itemIcon}
            level={1}
            showStatus={showStatus}
            status={getItemStatus(item)}
            getDisplayName={getDisplayName}
            showMetadataColumns={showMetadataColumns}
            uppercaseItemName={uppercaseItemName}
            isRemoving={removingItemId === item.id}
          />
        ))}
      </React.Fragment>
    );
  };

  const renderNestedLocalGroup = (parentKey, subgroups) => {
    const isParentExpanded = expandedLocalGroups.has(parentKey);
    const parentCount = Object.values(subgroups).reduce(
      (sum, items) => sum + (Array.isArray(items) ? items.length : 0),
      0
    );

    return (
      <React.Fragment key={parentKey}>
        <GroupRow
          label={getLocalGroupLabel(parentKey)}
          count={parentCount}
          isExpanded={isParentExpanded}
          onToggle={() => onToggleLocalGroup(parentKey)}
          icon={Folder}
          iconColor={localGroupIconColor}
          level={0}
          columnSpan={columnSpan}
        />

        {isParentExpanded &&
          Object.entries(subgroups).map(([subKey, items]) => {
            const subgroupKey = `${parentKey}.${subKey}`;
            const isSubExpanded = expandedLocalGroups.has(subgroupKey);
            const canAddSubgroup =
              onLocalSubgroupAdd && subKey !== 'data-catalog';

            return (
              <React.Fragment key={subgroupKey}>
                <GroupRow
                  label={getLocalSubgroupLabel(parentKey, subKey)}
                  count={items.length}
                  isExpanded={isSubExpanded}
                  onToggle={() => onToggleLocalGroup(subgroupKey)}
                  onAdd={
                    canAddSubgroup
                      ? () => onLocalSubgroupAdd(parentKey, subKey)
                      : null
                  }
                  icon={Folder}
                  iconColor={localGroupIconColor}
                  level={1}
                  columnSpan={columnSpan}
                />

                {isSubExpanded && items.length === 0 && getLocalSubgroupEmptyMessage?.(parentKey, subKey) ? (
                  <tr className="border-b border-gray-50/50 dark:border-gray-800/30">
                    <td className="px-5 py-3" colSpan={columnSpan}>
                      <p
                        className="text-[10px] font-medium text-gray-400 dark:text-gray-500"
                        style={{ paddingLeft: '4.5rem' }}
                      >
                        {getLocalSubgroupEmptyMessage(parentKey, subKey)}
                      </p>
                    </td>
                  </tr>
                ) : null}

                {isSubExpanded &&
                  items.map(item => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      onSelect={onSelectItem}
                      onRemove={onRemoveItem}
                      onDownload={onDownloadItem}
                      onRetry={onRetryItem}
                      formatDate={formatDate}
                      itemIcon={itemIcon}
                      level={2}
                      showStatus={showStatus}
                      status={getItemStatus(item)}
                      getDisplayName={getDisplayName}
                      showMetadataColumns={showMetadataColumns}
                      uppercaseItemName={uppercaseItemName}
                      isRemoving={removingItemId === item.id}
                    />
                  ))}
              </React.Fragment>
            );
          })}
      </React.Fragment>
    );
  };

  const renderLocalGroups = () => {
    return Object.entries(localGroups).map(([groupKey, groupValue]) =>
      isNestedLocalGroup(groupValue)
        ? renderNestedLocalGroup(groupKey, groupValue)
        : renderFlatLocalGroup(groupKey, groupValue)
    );
  };

  const renderBigQueryTree = () => {
    return Object.entries(bigQueryTree).map(([project, datasets]) => {
      const isProjectExpanded = expandedProjects.has(project);
      const projectCount = Object.values(datasets).reduce((sum, items) => sum + items.length, 0);

      return (
        <React.Fragment key={project}>
          <GroupRow
            label={project}
            count={projectCount}
            isExpanded={isProjectExpanded}
            onToggle={() => onToggleProject(project)}
            onAdd={onProjectAdd ? () => onProjectAdd(project) : null}
            onSettings={onProjectSettings ? () => onProjectSettings(project) : null}
            onUnlink={onProjectUnlink ? () => onProjectUnlink(project) : null}
            onReauth={onProjectReauth ? () => onProjectReauth(project) : null}
            sourceLabel="BigQuery"
            reauthRequired={bigQueryReauthRequired}
            disabled={isBigQueryLocked}
            disabledLabel={isBigQueryLocked ? "Upgrade required" : null}
            icon={Folder}
            iconColor={projectIconColor}
            level={0}
            columnSpan={columnSpan}
          />

          {isProjectExpanded && Object.entries(datasets).map(([dataset, items]) => {
            const datasetKey = `${project}.${dataset}`;
            const isDatasetExpanded = expandedDatasets.has(datasetKey);

            return (
              <React.Fragment key={datasetKey}>
                <GroupRow
                  label={dataset}
                  count={items.length}
                  isExpanded={isDatasetExpanded}
                  onToggle={(e) => {
                    e.stopPropagation();
                    onToggleDataset(project, dataset);
                  }}
                  icon={Folder}
                  iconColor={datasetIconColor}
                  level={1}
                  columnSpan={columnSpan}
                />

                {isDatasetExpanded && items.map(item => (
                  <ItemRow
                    key={item.id}
                    item={{ ...item, name: getItemName(item) }}
                    onSelect={onSelectItem}
                    onRemove={onRemoveItem}
                    onRetry={onRetryItem}
                    formatDate={formatDate}
                    itemIcon={itemIcon}
                    level={2}
                    showStatus={showStatus}
                    status={getItemStatus(item)}
                    getDisplayName={getDisplayName}
                    showMetadataColumns={showMetadataColumns}
                    uppercaseItemName={uppercaseItemName}
                    disabled={isBigQueryLocked}
                    disabledReason={
                      isBigQueryLocked
                        ? "This BigQuery dataset is from a previous plan. Upgrade to access it."
                        : null
                    }
                    isRemoving={removingItemId === item.id}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      );
    });
  };

  const renderCustomGroup = () => {
    if (!customGroup || customGroup.length === 0) return null;

    return (
      <React.Fragment>
        <tr
          className="bg-gray-50/20 dark:bg-gray-950/20 hover:bg-gray-100/30 dark:hover:bg-gray-900/30 cursor-pointer border-b border-gray-100/50 dark:border-gray-800/50"
        >
          <td className="px-5 py-2" colSpan={columnSpan}>
            <div className="flex items-center gap-2">
              <Folder size={13} className={localGroupIconColor} />
              <span className="text-[10px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Custom Functions
              </span>
              <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                ({customGroup.length})
              </span>
            </div>
          </td>
        </tr>
        {customGroup.map(item => (
          <ItemRow
            key={item.id}
            item={item}
            onSelect={onSelectItem}
            onRemove={onRemoveItem}
            formatDate={formatDate}
            itemIcon={itemIcon}
            level={1}
            showStatus={false}
            getDisplayName={(item) => item.signature || item.name}
            showMetadataColumns={showMetadataColumns}
            uppercaseItemName={uppercaseItemName}
          />
        ))}
      </React.Fragment>
    );
  };

  return (
    <>
      {renderLocalGroups()}
      {renderCustomGroup()}
      {renderBigQueryTree()}
    </>
  );
};

export default TreeTable;
